import { useApiClient } from "@/hooks/useApiClient";
import { type GeoSpaceChiban } from "@/features/map/types";
import {
  type TrustartLookupResponse,
  type TrustartParcelResult,
  type TrustartShapeResponse,
} from "@/features/map/types/trustart";

interface TrustartParcelApi {
  /** 地番→住所検索（住所込みの全文字列を渡す）。 */
  trustartSearch: (
    chiban: string
  ) => Promise<TrustartLookupResponse | undefined>;
  /** 住所→地番検索（BE 内で Google ジオコーディングを1回実行）。 */
  trustartReverse: (
    address: string
  ) => Promise<TrustartLookupResponse | undefined>;
  /** クリック検索（座標→地番、ST_Contains のみ）。 */
  trustartReverseByLatLng: (
    lat: number,
    lng: number
  ) => Promise<TrustartLookupResponse | undefined>;
  /** 範囲検索（描画ポリゴン内の筆一覧）。geospace と同形式の polygon 文字列を渡す。 */
  trustartReverseByPolygon: (
    polygon: string,
    signal?: AbortSignal
  ) => Promise<GeoSpaceChiban[]>;
  /** 範囲検索（描画円内の筆一覧）。中心座標と半径(m)を渡す。 */
  trustartReverseByCircle: (
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ) => Promise<GeoSpaceChiban[]>;
}

/**
 * TRUSTART 地番図のヒット結果を既存の GeoSpaceChiban 形式に変換する。
 *
 * 既存の検索結果表示パイプライン（PopupContent・ポリゴン描画・テキストボックス）を
 * そのまま再利用するためのマッパー。住居表示（address）は BE が "" を返すため空となる。
 */
export const trustartParcelToGeoSpaceChiban = (
  result: TrustartParcelResult
): GeoSpaceChiban => {
  const chiban = result.chiban ?? "";
  return {
    prefName: result.prefName,
    cityName: result.cityName,
    ooazaName: result.ooazaName || null,
    azaName: result.azaName || null,
    chiban,
    addressChiban: `${result.location}${chiban}`,
    polygon: result.polygon ?? undefined,
    lat: result.lat,
    lng: result.lng,
    address: result.addressResidence,
    area: result.area,
  };
};

/**
 * TRUSTART 地番図の検索系 API（既存 useGeoSpaceApi と対称の命名）を提供するフック。
 *
 * フォールバック用途のため、いずれも throw せず envelope または undefined を返す。
 * 呼び出し側は status（success / not_available / not_found / failed）で分岐する。
 */
export const useTrustartParcelApi = (): TrustartParcelApi => {
  const { apiClient } = useApiClient();

  const trustartSearch = async (
    chiban: string
  ): Promise<TrustartLookupResponse | undefined> => {
    try {
      const response = await apiClient.get<TrustartLookupResponse>(
        "/trustart/search",
        { params: { chiban } }
      );
      return response.data;
    } catch (e) {
      return undefined;
    }
  };

  const trustartReverse = async (
    address: string
  ): Promise<TrustartLookupResponse | undefined> => {
    try {
      const response = await apiClient.get<TrustartLookupResponse>(
        "/trustart/reverse",
        { params: { address } }
      );
      return response.data;
    } catch (e) {
      return undefined;
    }
  };

  const trustartReverseByLatLng = async (
    lat: number,
    lng: number
  ): Promise<TrustartLookupResponse | undefined> => {
    try {
      const response = await apiClient.get<TrustartLookupResponse>(
        "/trustart/reverse-latlng",
        { params: { lat, lng } }
      );
      return response.data;
    } catch (e) {
      return undefined;
    }
  };

  const trustartReverseByPolygon = async (
    polygon: string,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[]> => {
    try {
      const response = await apiClient.get<TrustartShapeResponse>(
        "/trustart/reverse-polygon",
        { params: { polygon }, signal }
      );
      return response.data.results.map(trustartParcelToGeoSpaceChiban);
    } catch (e) {
      return [];
    }
  };

  const trustartReverseByCircle = async (
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[]> => {
    try {
      const response = await apiClient.get<TrustartShapeResponse>(
        "/trustart/reverse-circle",
        { params: { radius, lat, lng }, signal }
      );
      return response.data.results.map(trustartParcelToGeoSpaceChiban);
    } catch (e) {
      return [];
    }
  };

  return {
    trustartSearch,
    trustartReverse,
    trustartReverseByLatLng,
    trustartReverseByPolygon,
    trustartReverseByCircle,
  };
};
